import { ComponentProps, forwardRef, useImperativeHandle, useRef } from "react";
import { GestureResponderEvent } from "react-native";
import {
  ButtonProps,
  styled,
  Button as TGButton,
  ButtonText as TGButtonText,
} from "tamagui";

// All properties from the StyledButtonText variant should correspond to those in the StyledButton variant, and StyledButtonText should inherit the same font properties as the StyledButton size.
type Variant =
  | "primary"
  | "gray"
  | "outlined"
  | "transparent"
  | "danger"
  | "pink";
type Size = "sm" | "md" | "lg";
type Radius = "sm" | "md" | "lg" | "xl";
type Mode = "button" | "icon";

export const StyledButtonText = styled(TGButtonText, {
  name: "StyledButtonText",
  cursor: "pointer",
  variants: {
    variant: {
      primary: {
        color: "white",
        pressStyle: {
          color: "$gray-secondary-400",
        },
        disabledStyle: {
          color: "$gray-secondary-400",
          cursor: "not-allowed",
        },
      },

      gray: {
        color: "$gray-primary-900",

        disabledStyle: {
          opacity: 0.3,
          color: "$gray-400",
          cursor: "not-allowed",
        },
      },

      outlined: {
        color: "$gray-primary-900",

        disabledStyle: {
          opacity: 0.3,
          color: "$gray-400",
          cursor: "not-allowed",
        },
      },

      danger: {
        color: "$error-500",

        disabledStyle: {
          opacity: 0.3,
          color: "$error-500",
          cursor: "not-allowed",
        },
      },

      pink: {
        color: "$darkpink-500",

        disabledStyle: {
          opacity: 0.3,
          color: "$pinkmain-500",
          cursor: "not-allowed",
        },
      },

      transparent: {
        color: "$gray-primary-900",

        disabledStyle: {
          opacity: 0.3,
          color: "$gray-400",
          cursor: "not-allowed",
        },
      },
    },

    font: {
      lg: {
        fontFamily: "$subtitle",
        fontSize: "$5",
        fontWeight: "$5",
      },
      md: {
        fontFamily: "$subtitle",
        fontSize: "$3",
        fontWeight: "$5",
      },
      sm: {
        fontFamily: "$subtitle",
        fontSize: "$2",
        fontWeight: "$5",
      },
    },
  } as const,

  defaultVariants: {
    variant: "primary",
    font: "md",
  },
});

const buttonVariants = {
  primary: {
    backgroundColor: "$green-500",
    color: "white",
    focusable: true,
    focusStyle: {
      borderColor: "white",
      outlineWidth: "$0.1",
      outlineColor: "$lightblue-800",
    },
    hoverStyle: {
      backgroundColor: "$lightgreen-500",
    },
    pressStyle: {
      backgroundColor: "$darkgreen-500",
    },
    disabledStyle: {
      backgroundColor: "$gray-100",
      cursor: "not-allowed",
    },
  },

  gray: {
    borderWidth: "$0.2",
    backgroundColor: "$gray-50",
    borderColor: "$gray-200",
    focusable: true,
    focusStyle: {
      outlineWidth: 0,
      backgroundColor: "white",
      borderColor: "$gray-secondary-400",
    },
    hoverStyle: {
      borderColor: "$gray-600",
    },
    pressStyle: {
      backgroundColor: "$gray-100",
    },
    disabledStyle: {
      borderWidth: 0,
      backgroundColor: "$gray-100",
      cursor: "not-allowed",
    },
  },

  outlined: {
    borderColor: "$gray-primary-900",
    borderWidth: "$0.2",
    backgroundColor: "white",
    focusable: true,
    focusStyle: {
      outlineWidth: 0,
      backgroundColor: "white",
      borderColor: "black",
    },
    hoverStyle: {
      borderColor: "$gray-secondary-400",
      backgroundColor: "$gray-50",
    },
    pressStyle: {
      backgroundColor: "$gray-200",
      borderColor: "black",
    },
    disabledStyle: {
      borderWidth: 0,
      backgroundColor: "$gray-100",
      cursor: "not-allowed",
    },
  },

  danger: {
    borderColor: "$error-500",
    borderWidth: "$0.2",
    backgroundColor: "transparent",
    focusable: true,
    //TODO: button state
    focusStyle: {
      outlineWidth: 0,
      backgroundColor: "transparent",
    },
    hoverStyle: {
      borderColor: "$error-500",
      backgroundColor: "transparent",
    },
    pressStyle: {
      borderColor: "$error-500",
      backgroundColor: "transparent",
    },
    disabledStyle: {
      borderWidth: 0,
      backgroundColor: "transparent",
      cursor: "not-allowed",
    },
  },

  pink: {
    borderColor: "$darkpink-500",
    borderWidth: "$0.2",
    backgroundColor: "transparent",
    focusable: true,
    focusStyle: {
      outlineWidth: 0,
      backgroundColor: "transparent",
    },
    hoverStyle: {
      borderColor: "$pinkmain-500",
      backgroundColor: "$lightpink-100",
    },
    pressStyle: {
      borderColor: "$pinkmain-500",
      backgroundColor: "$lightpink-100",
    },
    disabledStyle: {
      borderWidth: 0,
      backgroundColor: "transparent",
      cursor: "not-allowed",
    },
  },

  transparent: {
    backgroundColor: "transparent",
    focusable: true,
    focusStyle: {
      borderWidth: "$0.2",
      outlineWidth: 0,
      backgroundColor: "white",
      borderColor: "$gray-secondary-400",
    },
    hoverStyle: {
      backgroundColor: "$gray-50",
    },
    pressStyle: {
      backgroundColor: "$gray-100",
    },
    disabledStyle: {
      borderWidth: 0,
      backgroundColor: "$gray-100",
      cursor: "not-allowed",
    },
  },
} satisfies Record<Variant, ButtonProps>;

export const StyledButton = styled(TGButton, {
  name: "StyledButton",
  cursor: "pointer",
  variants: {
    variant: buttonVariants,
    rounded: {
      xl: {
        borderRadius: "$7",
      },
      lg: {
        borderRadius: "$5",
      },
      md: {
        borderRadius: "$5", // This button type only uses one borderRadius value (lg). The repetition is needed to satisfy TypeScript and provide a radius option for the icon mode button.
      },
      sm: {
        borderRadius: "$2",
      },
    },

    sizes: {
      lg: {
        height: "$8",
        gap: "$1",
        paddingVertical: "$2",
        paddingHorizontal: "$4",
      },
      md: {
        height: "$6",
        gap: "$1",
        paddingVertical: "$1.5",
        paddingHorizontal: "$3",
      },
      sm: {
        height: "$5",
        gap: "$1",
        paddingVertical: "$1.5",
        paddingHorizontal: "$3",
      },
    },
  } as const,
  defaultVariants: {
    variant: "primary",
    sizes: "sm",
    rounded: "lg",
  },
});

export const StyledIconButton = styled(TGButton, {
  name: "StyledButton",
  cursor: "pointer",
  variants: {
    variant: buttonVariants,
    rounded: {
      xl: {
        borderRadius: "$7",
      },
      lg: {
        borderRadius: "$5",
      },
      md: {
        borderRadius: "$2",
      },
      sm: {
        borderRadius: "$2",
      },
    },
    sizes: {
      lg: {
        height: "$8",
        width: "$8",
        padding: "$2",
      },
      md: {
        height: "$5.8",
        width: "$5.8",
        padding: "$1",
      },
      sm: {
        height: "$4.1",
        width: "$4.1",
        padding: "$1",
      },
    },
  } as const,
  defaultVariants: {
    variant: "primary",
    sizes: "md",
    rounded: "md",
  },
});

type ButtonTextProps = ComponentProps<typeof StyledButtonText>;

export const ButtonText: React.FC<ButtonTextProps> = ({
  children,
  ...rest
}) => {
  if (!children) return null;
  return <StyledButtonText {...rest}>{children}</StyledButtonText>;
};

type StyledButtonProps = ComponentProps<typeof StyledButton>;

export type ButtonBaseProps = Omit<
  StyledButtonProps,
  "ref" | "variant" | "size" | "disabled" | "icon" | "iconAfter"
> & {
  disabled?: boolean;
  mode?: Mode;
  variant?: Variant;
  size?: Size;
  rounded?: Radius;
  icon?: React.ReactNode;
  iconAfter?: React.ReactNode;
  id?: string;
  ids?: string[];
};

export type ButtonRef = {
  press: () => void;
  focus: () => void;
  blur: () => void;
  id?: string | null;
  ids?: string[];
};

export const Button = forwardRef<ButtonRef, ButtonBaseProps>(
  (
    {
      children,
      variant,
      size,
      mode = "button",
      id,
      ids,
      rounded,
      disabled = false,
      icon,
      iconAfter,
      onPress,
      ...rest
    },
    fowardedRef,
  ) => {
    const ref = useRef<React.ElementRef<typeof StyledButton>>(null);

    // Extend the ref methods and props by adding a custom `press` method and `id` prop
    // so parent components can trigger press programmatically.
    // Note: other internal methods won’t be available unless explicitly exposed,
    // or be “clever” enough to use a proxy to forward them.
    // but do it only if you love spending time debugging ghost bugs. Otherwise, just don’t.
    useImperativeHandle(fowardedRef, () => {
      const el = ref.current;
      return {
        focus: () => el?.focus(),
        blur: () => el?.blur?.(),
        id: id ?? null,
        ids: ids,
        press: () => {
          if (disabled) return;
          onPress?.({
            nativeEvent: {},
            type: "press",
          } as GestureResponderEvent);
        },
      };
    }, [id, ids, disabled, onPress]);

    if (mode === "icon") {
      return (
        <StyledIconButton
          ref={ref}
          onPress={onPress}
          rounded={rounded}
          variant={variant}
          sizes={size}
          disabled={disabled}
          icon={children}
          {...rest}
        />
      );
    }

    return (
      <StyledButton
        ref={ref}
        rounded={rounded}
        variant={variant}
        disabled={disabled}
        onPress={onPress}
        sizes={size}
        {...rest}
      >
        {typeof children === "string" || typeof children === "number" ? (
          <>
            {icon}
            <ButtonText variant={variant} font={size} disabled={disabled}>
              {children}
            </ButtonText>
            {iconAfter}
          </>
        ) : (
          children
        )}
      </StyledButton>
    );
  },
);

Button.displayName = "Button";
